import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useDataGridFetcher } from '@/hooks/api/useDataGridFetcher';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import type { ListFolderContentsParams, ListRootContentsParams, SortOrder } from '@/api/generatedApi';
import type { SerializedFilters } from '@/utils/columnFilterSerializer';

export const LIST_FOLDER_CONTENTS_QUERY_KEY = 'folders.folderContents';

export const getFolderContentsQueryKey = ({
  folderId,
  page,
  perPage,
  tenantId,
  sortBy,
  sortOrder,
  sortByMetadataField,
  filters,
}: {
  folderId?: string;
  page?: string | number;
  perPage?: string | number;
  tenantId?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  sortByMetadataField?: string;
  filters?: SerializedFilters;
} = {}) => [
  LIST_FOLDER_CONTENTS_QUERY_KEY,
  {
    folderId,
    tenantId,
    page: +(page ?? 1),
    perPage: +(perPage ?? DATA_GRID_LAZY_PAGE_SIZE),
    sortBy,
    sortOrder,
    sortByMetadataField,
    filters,
  },
];

const queryFunction =
  ({
    folderId,
    page,
    perPage,
    tenantId,
    sortBy,
    sortOrder,
    sortByMetadataField,
    filters,
  }: {
    folderId?: string;
    page?: string | number;
    perPage?: string | number;
    tenantId?: string;
    sortBy?: string;
    sortOrder?: SortOrder;
    sortByMetadataField?: string;
    filters?: SerializedFilters;
  } = {}) =>
  () => {
    const baseParameters = {
      tenantId: tenantId ?? '',
      contentType: 'all',
      page: +(page ?? 1),
      perPage: +(perPage ?? DATA_GRID_LAZY_PAGE_SIZE),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(sortByMetadataField && { sortByMetadataField }),
      ...(filters && { filters: JSON.stringify(filters) }),
    };

    return folderId
      ? api.folders.listFolderContents({ ...baseParameters, folderId } as ListFolderContentsParams)
      : api.folders.listRootContents(baseParameters as ListRootContentsParams);
  };

export const useFetchFolderContents = ({ folderId }: { folderId?: string } = {}) => {
  const { getTenantId } = useTenants();
  return useDataGridFetcher({
    queryKey: parameters => getFolderContentsQueryKey({ tenantId: getTenantId(), folderId, ...parameters }),
    queryFn: parameters => queryFunction({ tenantId: getTenantId(), folderId, ...parameters }),
  });
};
